/**
 * Member Portal — My verified hours (WeaveIn Verify).
 *
 * Every line on the member's record for the month: date, hours, what, where,
 * and who filed it. The member can dispute any line (it stops counting until the
 * organization answers) and can add hours no organization can confirm — gig,
 * cash or informal work — which count as self-reported.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Flag, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getMemberToken, isAuthenticated } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { Statement, StatementState } from '@/components/verify/api';
import {
  HoursBar,
  RecordStatusBadge,
  StatementStateBadge,
  activityLabel,
  fmtDate,
  fmtHours,
  inputClass,
  monthName,
} from '@/components/verify/shared';

interface MemberLine {
  id: number;
  activity_date: string;
  hours: number;
  activity_type: string;
  service_description: string;
  site_name: string | null;
  point_of_contact_name: string | null;
  source: string;
  filed_by_label: string;
  status: 'captured' | 'confirmed' | 'self_attested' | 'disputed' | 'voided';
  dispute_reason: string | null;
  resolution: string | null;
  resolution_notes: string | null;
}

interface MemberStatementResponse {
  member: { first_name: string | null };
  enrollments: { id: number; organization_name: string; next_redetermination_date: string | null }[];
  statement: Statement & { lines: MemberLine[] };
  history: (Omit<Statement, 'lines'> & { state: StatementState })[];
}

const SELF_TYPES = ['employment', 'volunteer', 'community_service', 'job_training', 'education', 'job_search'];

async function memberFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getMemberToken();
  const res = await fetch(getWeaveinApiUrl(path), {
    ...init,
    headers: {
      Authorization: `Bearer ${token || ''}`,
      'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : '';
    } catch {
      // not JSON
    }
    const err = new Error(detail || `${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

const todayISO = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function MemberVerifiedHoursPage() {
  const { t, language } = useAppTranslation();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState<MemberStatementResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'denied' | 'none' | 'error'>('loading');
  const [disputing, setDisputing] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [entry, setEntry] = useState({ activity_type: 'employment', service_description: '', activity_date: todayISO(), hours: '', point_of_contact_name: '' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) navigate('/member/login');
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const r = await memberFetch<MemberStatementResponse>(`/api/verify-member/me?year=${period.year}&month=${period.month}`);
      setData(r);
      setState('ready');
    } catch (e) {
      const status = (e as { status?: number }).status;
      setState(status === 403 ? 'denied' : status === 404 ? 'none' : 'error');
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  const dispute = async (id: number) => {
    setBusy(true);
    setFormError(null);
    try {
      await memberFetch(`/api/verify-member/me/records/${id}/dispute`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setDisputing(null);
      setReason('');
      setNote(t('verify.member.disputed_note', 'Sent. That line will not count until the organization answers you.'));
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('verify.member.failed', 'That did not work. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const addHours = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await memberFetch('/api/verify-member/me/records', {
        method: 'POST',
        body: JSON.stringify({
          activity_type: entry.activity_type,
          service_description: entry.service_description.trim(),
          activity_date: entry.activity_date,
          hours: Number(entry.hours),
          point_of_contact_name: entry.point_of_contact_name.trim() || undefined,
        }),
      });
      setAdding(false);
      setEntry({ ...entry, service_description: '', hours: '', point_of_contact_name: '' });
      setNote(t('verify.member.added_note', 'Added. It counts as self-reported.'));
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('verify.member.failed', 'That did not work. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const months = useMemo(() => {
    const out = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
    return out;
  }, [now]);

  const header = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('verify.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('verify.member.title', 'My verified hours')}
      description={t('verify.member.desc', 'Every hour on your record, who filed it, and whether it counts.')}
    />
  );

  if (state !== 'ready' || !data) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          {state === 'loading' ? (
            <PageState variant="loading" />
          ) : state === 'denied' ? (
            <PageState
              variant="denied"
              title={t('verify.member.not_available_title', 'Not available yet')}
              message={t('verify.member.not_available', 'Verified hours are not available for your organization.')}
            />
          ) : state === 'none' ? (
            <PageState
              variant="empty"
              message={t('verify.member.no_record', 'We could not find your member record. Please contact your organization.')}
            />
          ) : (
            <PageState
              variant="error"
              message={t('verify.member.load_failed', 'We could not load your hours.')}
              action={
                <Button variant="outline" size="sm" onClick={() => void load()}>
                  {t('verify.retry', 'Try again')}
                </Button>
              }
            />
          )}
        </PageContainer>
      </>
    );
  }

  const st = data.statement;
  const enrollment = data.enrollments[0];

  return (
    <>
      {header}
      <PageContainer size="wide">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {months.map((m) => {
              const active = m.year === period.year && m.month === period.month;
              return (
                <button
                  key={`${m.year}-${m.month}`}
                  type="button"
                  onClick={() => setPeriod(m)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    active ? 'border-v2accent bg-v2accent-soft text-v2accent-ink' : 'border-v2border text-ink-700'
                  }`}
                >
                  {monthName(m.year, m.month, language)}
                </button>
              );
            })}
          </div>

          <Card>
            <CardContent className="space-y-3 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-display text-3xl font-semibold text-ink">
                  {fmtHours(st.counted_hours)}
                  <span className="ml-2 text-base font-normal text-ink-500">
                    {t('verify.member.of_80', 'of {{n}} hours', { n: st.required_hours })}
                  </span>
                </div>
                <StatementStateBadge state={st.state} t={t} />
              </div>
              <HoursBar counted={st.counted_hours} verified={st.verified_hours} pending={st.pending_hours} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-700">
                <span>
                  {t('verify.member.confirmed', 'Confirmed by an organization')}: {fmtHours(st.verified_hours)}
                </span>
                <span>
                  {t('verify.member.self', 'You reported')}: {fmtHours(st.self_attested_hours)}
                </span>
                {st.pending_hours > 0 && (
                  <span>
                    {t('verify.member.waiting', 'Waiting for confirmation')}: {fmtHours(st.pending_hours)}
                  </span>
                )}
                {st.disputed_hours > 0 && (
                  <span>
                    {t('verify.member.disputed', 'You disputed')}: {fmtHours(st.disputed_hours)}
                  </span>
                )}
              </div>
              {enrollment?.next_redetermination_date && (
                <div className="text-sm text-ink-500">
                  {t('verify.member.renewal', 'Your next Medicaid renewal with {{org}} is due {{date}}.', {
                    org: enrollment.organization_name,
                    date: fmtDate(enrollment.next_redetermination_date),
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {note && (
            <div className="flex items-center gap-2 rounded-md bg-success-soft px-3 py-2 text-sm text-success-ink">
              <CheckCircle2 className="h-4 w-4" />
              {note}
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">{t('verify.member.lines', 'Every line this month')}</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('verify.member.add', 'Add hours you did on your own')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {adding && (
                <div className="space-y-3 rounded-md border border-v2border p-3">
                  <p className="text-sm text-ink-500">
                    {t(
                      'verify.member.add_desc',
                      'For work no organization can confirm — gig, cash or informal work. These count as self-reported.',
                    )}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <select
                      className={inputClass}
                      value={entry.activity_type}
                      onChange={(e) => setEntry({ ...entry, activity_type: e.target.value })}
                    >
                      {SELF_TYPES.map((a) => (
                        <option key={a} value={a}>
                          {activityLabel(t, a)}
                        </option>
                      ))}
                    </select>
                    <input
                      className={inputClass}
                      placeholder={t('verify.member.what', 'What you did')}
                      value={entry.service_description}
                      onChange={(e) => setEntry({ ...entry, service_description: e.target.value })}
                    />
                    <input
                      className={inputClass}
                      type="date"
                      max={todayISO()}
                      value={entry.activity_date}
                      onChange={(e) => setEntry({ ...entry, activity_date: e.target.value })}
                    />
                    <input
                      className={inputClass}
                      type="number"
                      min={0.25}
                      max={24}
                      step={0.25}
                      placeholder={t('verify.hours', 'Hours')}
                      value={entry.hours}
                      onChange={(e) => setEntry({ ...entry, hours: e.target.value })}
                    />
                    <input
                      className={`${inputClass} sm:col-span-2`}
                      placeholder={t('verify.member.who_paid', 'Who you worked for (optional)')}
                      value={entry.point_of_contact_name}
                      onChange={(e) => setEntry({ ...entry, point_of_contact_name: e.target.value })}
                    />
                  </div>
                  {formError && <div className="text-sm text-danger-ink">{formError}</div>}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
                      {t('verify.cancel', 'Cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void addHours()}
                      disabled={busy || entry.service_description.trim().length < 2 || !(Number(entry.hours) > 0)}
                    >
                      {t('verify.member.add_save', 'Add')}
                    </Button>
                  </div>
                </div>
              )}

              {st.lines.length === 0 ? (
                <p className="py-4 text-center text-sm text-ink-500">{t('verify.member.no_lines', 'No hours recorded this month yet.')}</p>
              ) : (
                st.lines.map((l) => (
                  <div key={l.id} className="rounded-md border border-v2border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-ink">
                          {fmtDate(l.activity_date)} · {fmtHours(l.hours)}
                        </div>
                        <div className="text-sm text-ink-700">
                          {activityLabel(t, l.activity_type)} — {l.service_description}
                        </div>
                        <div className="text-xs text-ink-500">
                          {[l.site_name, l.point_of_contact_name].filter(Boolean).join(' · ') || ''}
                          {' · '}
                          {t('verify.member.filed_by', 'Filed by {{who}}', { who: l.filed_by_label })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <RecordStatusBadge status={l.status} t={t} />
                        {l.status !== 'disputed' && l.status !== 'voided' && disputing !== l.id && (
                          <Button size="sm" variant="ghost" onClick={() => setDisputing(l.id)}>
                            <Flag className="mr-1 h-3.5 w-3.5" />
                            {t('verify.member.dispute', 'Dispute')}
                          </Button>
                        )}
                      </div>
                    </div>
                    {l.status === 'disputed' && l.dispute_reason && (
                      <div className="mt-2 text-sm text-ink-700">
                        {t('verify.member.you_said', 'You said:')} “{l.dispute_reason}”
                      </div>
                    )}
                    {l.resolution && l.resolution_notes && (
                      <div className="mt-2 rounded bg-paper-2 px-2 py-1 text-sm text-ink-700">
                        {t('verify.member.answer', 'The organization answered:')} {l.resolution_notes}
                      </div>
                    )}
                    {disputing === l.id && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          className={`${inputClass} min-h-[64px]`}
                          placeholder={t('verify.member.dispute_ph', 'What is wrong with this line?')}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                        {formError && <div className="text-sm text-danger-ink">{formError}</div>}
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setDisputing(null)}>
                            {t('verify.cancel', 'Cancel')}
                          </Button>
                          <Button size="sm" onClick={() => void dispute(l.id)} disabled={busy || reason.trim().length < 3}>
                            {t('verify.member.send_dispute', 'Send dispute')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </>
  );
}
